import { useState } from "react";
import { Link } from "react-router-dom";

import { useMe } from "@/api";

import { pickGreeting, type GreetingCase, type GreetingHandover } from "./pickGreeting";
import styles from "./StartScreen.module.css";

interface GreetingProps {
  /** The caller's cases, or `null` when the list could not be read. */
  cases: readonly GreetingCase[] | null;
  /** Requests to me that wait — named first (UX gate round 1, #1). */
  handovers?: readonly GreetingHandover[] | null;
  /** My Vorgänge whose calculation a blocker keeps from release — not counted as waiting for it. */
  blocked?: ReadonlySet<string> | null;
  isFirstVisit: boolean;
}

/**
 * The top of the start page (design 2026-09-07 §4, revised 23.09.2026): the
 * salutation by name and hour, and one sentence about what concretely waits
 * — with the case to continue with on the same line. Two lines, no more (UX
 * gate round 1, #1): the work stands right under it. The Spruch des Tages
 * moved to the foot of the page (`StartScreen`).
 *
 * **What is frozen, and what is not.** The *draw* is frozen in `useState`'s
 * initialiser — the clock reading, the random number for the idle sentence,
 * and whether this is a first visit — so a refetch cannot swap one idle line
 * for another under the reader's eyes. The *facts* are not frozen: the
 * waiting sentence is derived from the live list on every render. Freezing it
 * would leave „das Angebot kann raus" standing after the reader has ticked
 * „Angebot raus" in the register or the record — a claim the page itself has
 * just made false. The name is not frozen either: `useMe()` can still be in flight
 * on the first render.
 *
 * Nothing is rendered until there *is* a name. Past `RequireSession` that is
 * a single frame at most, and a greeting that addresses nobody is worse than
 * one that is a beat late.
 */
export function Greeting({ cases, handovers = null, blocked = null, isFirstVisit }: GreetingProps) {
  const me = useMe();
  const firstName = (me.data?.displayName ?? "").trim().split(/\s+/)[0] || "";

  const [draw] = useState(() => ({ now: new Date(), seed: Math.random(), isFirstVisit }));

  if (!firstName) return <div className={styles.greetingSpace} />;

  const greeting = pickGreeting(
    {
      hour: draw.now.getHours(),
      weekday: draw.now.getDay(),
      firstName,
      isFirstVisit: draw.isFirstVisit,
      cases,
      handovers,
      blocked,
    },
    () => draw.seed,
  );

  return (
    <header className={styles.greeting}>
      {/* The start page's title, behind the mesh marker of „Stahl" (design
          2026-10-24 §8.6) — the page's one red one (Task 11, B-11). */}
      <h1 className={styles.salutation}>
        <span className="marke marke-titel" aria-hidden="true" />
        {greeting.salutation}
      </h1>
      {/* Sentence and „Weiter mit …" share the second line where it is wide
          enough, and the link wraps under the sentence where it is not. */}
      <div className={styles.lineRow}>
        <p className={styles.line} data-testid="greeting-line">
          <LineWithFigure line={greeting.line} figure={greeting.figure} />
        </p>
        {greeting.focus ? (
          <Link className={styles.focus} to={greeting.focus.path}>
            Weiter mit <span className="num">{greeting.focus.caseNo}</span>
            {greeting.focus.title ? ` · ${greeting.focus.title}` : ""}
            <span aria-hidden="true"> →</span>
          </Link>
        ) : null}
      </div>
    </header>
  );
}

/** The case number sits in the figure face, like every ID on this app. */
function LineWithFigure({ line, figure }: { line: string; figure: string | null }) {
  const at = figure ? line.indexOf(figure) : -1;
  if (!figure || at < 0) return <>{line}</>;
  return (
    <>
      {line.slice(0, at)}
      <span className="num">{figure}</span>
      {line.slice(at + figure.length)}
    </>
  );
}
